import React, { useState, useEffect, useCallback } from 'react';
import BookmarksView from './BookmarksView';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { usePanelZoom, PANEL_ZOOM } from '../../hooks/usePanelZoom';
import './Bookmarks.css';

const MIN_WIDTH = 320;
const MAX_WIDTH = 500;

/**
 * BookmarksPanel - standalone side-panel chrome (resize, header) around
 * BookmarksView. The bookmarks feature now lives canonically as a tab inside the
 * Compendium (bible-consolidation); this wrapper is retained for any surface that
 * still mounts bookmarks as a dedicated panel.
 */
function BookmarksPanel({ storyId, projectId, width = 380, onWidthChange, onClose }) {
  const [isResizing, setIsResizing] = useState(false);
  const [panelZoom, setPanelZoom] = usePanelZoom();

  const isProjectContext = !storyId && projectId;
  const scopeLabel = isProjectContext ? 'Series' : 'Story';

  // Resize handling
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      onWidthChange?.(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onWidthChange]);

  return (
    <div className="bookmarks-panel" style={{ width }}>
      {/* Resize Handle */}
      <div
        className={`bookmarks-panel__resize-handle ${isResizing ? 'bookmarks-panel__resize-handle--active' : ''}`}
        onMouseDown={handleResizeStart}
      />

      {/* Header */}
      <div className="bookmarks-panel__header">
        <h2 className="bookmarks-panel__title">Bookmarks</h2>
        <span className="bookmarks-panel__scope">{scopeLabel}</span>
        <TextSizeStepper
          value={panelZoom}
          onChange={setPanelZoom}
          min={PANEL_ZOOM.min}
          max={PANEL_ZOOM.max}
          step={PANEL_ZOOM.step}
          label="Panel text size"
        />
        <button
          className="bookmarks-panel__close"
          onClick={onClose}
          aria-label="Close bookmarks panel"
        >
          ×
        </button>
      </div>

      {/* Magnified body (Item 2: shared panel zoom). */}
      <div className="panel-zoom" style={{ zoom: panelZoom / 100 }}>
        <BookmarksView storyId={storyId} projectId={projectId} />
      </div>
    </div>
  );
}

export default BookmarksPanel;
